import React, { useState, useRef, useEffect } from "react";
import { Search, ChevronDown, Check, Loader2, X } from "lucide-react";
import ErrorMessage from "./ErrorMessage";

export interface Option {
    value: string;
    label: string;
    [key: string]: any;
}

interface SearchSelectProps {
    label?: string;
    error?: string;
    placeholder?: string;
    options: Option[];
    value?: string;
    onChange: (value: string) => void;
    className?: string;
    disabled?: boolean;
    required?: boolean;
    onSearch?: (searchTerm: string) => void;
    isLoading?: boolean;
    debounceMs?: number;
    allowClear?: boolean;
}

const SearchSelect: React.FC<SearchSelectProps> = ({
    label,
    error,
    placeholder = "Select option...",
    options = [],
    value,
    onChange,
    className,
    disabled = false,
    required = false,
    onSearch,
    isLoading = false,
    debounceMs = 350,
    allowClear = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCache, setSelectedCache] = useState<Option | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastSearchedTermRef = useRef<string>("");
    const onSearchRef = useRef(onSearch);

    useEffect(() => {
        onSearchRef.current = onSearch;
    }, [onSearch]);

    // Keep cached selected option so label stays visible even if option list is filtered remotely
    useEffect(() => {
        if (value) {
            const found = options.find(opt => opt.value === value);
            if (found) {
                setSelectedCache(found);
            }
        } else {
            setSelectedCache(null);
        }
    }, [value, options]);

    const toggleDropdown = () => {
        if (!disabled) setIsOpen(!isOpen);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setSearchTerm("");
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            // Reset search state and reload full list when dropdown closes
            // This prevents stale "No data found" when reopening after a failed search
            if (lastSearchedTermRef.current !== "") {
                lastSearchedTermRef.current = "";
                onSearchRef.current?.("");
            }
        }
    }, [isOpen]);

    // Handle user typing in search input
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawQuery = e.target.value;
        setSearchTerm(rawQuery);

        if (!onSearchRef.current) return;

        const trimmedQuery = rawQuery.trim().toLocaleLowerCase();

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            // Only trigger API if trimmedQuery has actually changed
            if (lastSearchedTermRef.current !== trimmedQuery) {
                lastSearchedTermRef.current = trimmedQuery;
                onSearchRef.current?.(trimmedQuery);
            }
        }, debounceMs);
    };

    const handleClearSearch = () => {
        setSearchTerm("");
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        if (lastSearchedTermRef.current !== "") {
            lastSearchedTermRef.current = "";
            onSearchRef.current?.("");
        }
    };

    const validOptions = options.filter(opt => opt.value !== "" || !searchTerm.trim());
    const filteredOptions = onSearch
        ? options.filter(opt => opt.value !== "")
        : validOptions.filter(option =>
            option?.label?.toLowerCase().includes(searchTerm.toLowerCase().trim())
        );

    const selectedOption = options.find(opt => opt.value === value) || selectedCache;

    const handleSelect = (val: string) => {
        onChange(val);
        setIsOpen(false);
        setSearchTerm("");
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange("");
        setSelectedCache(null);
    };

    return (
        <div className={`relative  ${className || ""}`} ref={containerRef}>
            {label && (
                <label className="inline-block text-sm font-medium mb-2 text-gray-700">
                    {label}
                    {required && <span className="text-destructive ml-0.5">*</span>}
                </label>
            )}

            <div
                onClick={toggleDropdown}
                className={`flex h-9 w-full items-center justify-between rounded-lg border border-input bg-white px-3 py-2 text-sm shadow-sm transition-colors cursor-pointer ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-primary/50"
                    } ${isOpen ? "ring-1 ring-primary border-primary" : ""}`}
            >
                <span className={`truncate mr-2 ${!selectedOption?.label ? "text-muted-foreground" : "text-slate-800"}`}>
                    {selectedOption?.label ? selectedOption.label : placeholder}
                </span>

                <div className="flex items-center gap-1.5 shrink-0">
                    {allowClear && value && !disabled && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="p-0.5 text-muted-foreground hover:text-destructive rounded-full hover:bg-slate-100 transition-colors"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                    <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                </div>
            </div>

            {isOpen && (
                <div className="absolute z-50 mt-1 w-full rounded-lg border bg-white shadow-lg shadow-black/10 animate-in fade-in-0 zoom-in-95">
                    <div className="flex items-center border-b px-3 py-2">
                        {isLoading ? (
                            <Loader2 className="mr-2 h-4 w-4 shrink-0 text-primary animate-spin" />
                        ) : (
                            <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                        )}
                        <input
                            className="flex h-8 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                            placeholder="Search..."
                            autoFocus
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                onClick={handleClearSearch}
                                className="p-1 text-muted-foreground hover:text-slate-700"
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </div>

                    <div className="max-h-60 overflow-y-auto p-1">
                        {isLoading ? (
                            <div className="py-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                                <span>Searching...</span>
                            </div>
                        ) : filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <div
                                    key={option.value}
                                    onClick={() => handleSelect(option.value)}
                                    className={`relative flex cursor-pointer select-none items-center rounded-md px-2.5 py-2 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground ${option.value === value ? "bg-accent/80 font-medium text-accent-foreground" : ""
                                        }`}
                                >
                                    <span className="flex-1 truncate">{option.label}</span>
                                    {option.value === value && (
                                        <Check className="h-4 w-4 text-primary shrink-0 ml-2" />
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="py-6 text-center text-sm text-muted-foreground">
                                No data found
                            </div>
                        )}
                    </div>
                </div>
            )}

            <ErrorMessage errors={error} />
        </div>
    );
};

export default SearchSelect;